import { z } from 'zod';
import type {
  PlatformFlags,
  MaintenanceState,
  AnnouncementAudience,
  AnnouncementLevel,
} from './platform';

/*
 * The owner's admin panel (admin.teaplus.tv). Everything here is only served on the admin host,
 * to the one owner, after a password and a passkey.
 */

export const ADMIN_PASSWORD_MIN = 14;

export const adminPasswordSchema = z.object({ password: z.string().min(1).max(200) });
export const adminEnrollStartSchema = z.object({
  token: z.string().min(20).max(100),
  /** The current password when an owner exists, or the new one for the first enrolment. */
  password: z.string().min(1).max(200),
});
export const adminEnrollFinishSchema = z.object({
  token: z.string().min(20).max(100),
  name: z.string().trim().min(1).max(60),
  response: z.record(z.string(), z.unknown()),
});
export const adminPasskeySchema = z.object({ response: z.record(z.string(), z.unknown()) });
export const adminChangePasswordSchema = z.object({
  current: z.string().min(1).max(200),
  next: z.string().min(ADMIN_PASSWORD_MIN).max(200),
});
export const adminRenamePasskeySchema = z.object({ name: z.string().trim().min(1).max(60) });

export const adminUserStatusSchema = z.object({
  status: z.enum(['active', 'restricted', 'suspended']),
  reason: z.string().trim().max(500).optional(),
});
export const adminUserStaffSchema = z.object({ staff: z.boolean() });
export const adminDeleteUserSchema = z.object({ confirmEmail: z.string().max(320) });
export const adminPasswordResetSchema = z.object({
  /** Also email the link to the member. */
  email: z.boolean().default(true),
  /** Sign them out everywhere now, rather than when they use the link. */
  signOut: z.boolean().default(false),
});
export interface AdminPasswordReset {
  link: string;
  expiresAt: string;
  /** False when the server has no email set up (Canary, development). */
  emailed: boolean;
  signedOut: number;
}
export const adminProfileStatusSchema = z.object({
  restricted: z.boolean(),
  reason: z.string().trim().max(500).optional(),
});

export const adminAnnouncementSchema = z.object({
  title: z.string().trim().min(1).max(120),
  body: z.string().trim().min(1).max(1000),
  level: z.enum(['info', 'success', 'warning', 'critical']).default('info'),
  audience: z.enum(['all', 'desktop', 'web']).default('all'),
  active: z.boolean().default(true),
  dismissible: z.boolean().default(true),
  startsAt: z.iso.datetime().nullable().optional(),
  endsAt: z.iso.datetime().nullable().optional(),
});
export const adminFlagsSchema = z.object({
  registrations: z.boolean().optional(),
  parties: z.boolean().optional(),
  friendRequests: z.boolean().optional(),
  chat: z.boolean().optional(),
  addonInstalls: z.boolean().optional(),
  webApp: z.boolean().optional(),
});
export const adminMaintenanceSchema = z.object({
  enabled: z.boolean(),
  message: z.string().trim().max(400).optional(),
  until: z.iso.datetime().nullable().optional(),
});
export const adminMinVersionSchema = z.object({
  version: z
    .string()
    .regex(/^\d+(\.\d+){1,3}$/)
    .nullable(),
});

/** ?from=&to= on analytics endpoints, as ISO dates. Defaults to the last 30 days. */
export const adminRangeSchema = z.object({
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
});

export const adminPageSchema = z.object({
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce.number().int().min(5).max(100).default(25),
});

export interface AdminSessionInfo {
  enrolled: boolean;
  authenticated: boolean;
  session: { id: string; createdAt: string; expiresAt: string; idleExpiresAt: string } | null;
}

export interface AdminPasskey {
  id: string;
  name: string;
  deviceType: string | null;
  backedUp: boolean;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface AdminDeviceInfo {
  client: 'desktop' | 'web';
  appVersion: string | null;
  os: string | null;
  browser: string | null;
}

export interface Paged<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface SeriesPoint {
  /** The day (YYYY-MM-DD) or hour (ISO) the value is for. */
  t: string;
  v: number;
}

export interface Ranked {
  key: string;
  label: string;
  value: number;
  /** A second figure, such as unique users next to installs. */
  extra?: number;
  imageUrl?: string | null;
  sub?: string | null;
}

/** Shown next to figures that only exist since the server started tracking them. */
export interface TrackedSince {
  telemetrySince: string;
}

export interface LiveEvent {
  id: string;
  at: string;
  kind: string;
  userId: string | null;
  profileId: string | null;
  who: string | null;
  text: string;
  client: string | null;
}

export interface LivePlayback {
  id: string;
  userId: string;
  profileId: string;
  profileName: string;
  avatar: string;
  avatarUrl: string | null;
  email: string;
  mediaId: string;
  contentType: string;
  title: string | null;
  season: number | null;
  episode: number | null;
  posterUrl: string | null;
  addonName: string | null;
  provider: string | null;
  client: string;
  appVersion: string | null;
  os: string | null;
  startedAt: string;
  lastBeatAt: string;
  position: number;
  duration: number;
  watchedSeconds: number;
  status: 'playing' | 'paused' | 'buffering';
  bufferCount: number;
  errorCount: number;
}

export interface LiveOnline {
  profileId: string;
  profileName: string;
  avatar: string;
  avatarUrl: string | null;
  userId: string;
  email: string;
  status: string;
  watching: string | null;
}

export interface AdminLiveSnapshot {
  at: string;
  onlineProfiles: number;
  onlineAccounts: number;
  streams: number;
  playbacks: LivePlayback[];
  online: LiveOnline[];
}

export interface AdminOverview extends TrackedSince {
  range: { from: string; to: string };
  totals: {
    users: number;
    profiles: number;
    addonsInstalled: number;
    debridConnected: number;
  };
  now: { onlineProfiles: number; onlineAccounts: number; streams: number };
  active: { dau: number; wau: number; mau: number };
  period: {
    registrations: number;
    registrationsPrev: number;
    plays: number;
    playsPrev: number;
    watchSeconds: number;
    watchSecondsPrev: number;
    activeUsers: number;
    activeUsersPrev: number;
  };
  series: {
    registrations: SeriesPoint[];
    users: SeriesPoint[];
    activeUsers: SeriesPoint[];
    plays: SeriesPoint[];
    watchHours: SeriesPoint[];
  };
  topTitles: Ranked[];
  topAddons: Ranked[];
  feed: LiveEvent[];
}

export interface AdminUserRow {
  id: string;
  email: string;
  displayName: string;
  status: string;
  staff: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  lastSeenAt: string | null;
  profiles: number;
  addons: number;
  provider: string | null;
  online: boolean;
  client: string | null;
  /** The linked Discord username, if any. */
  discord: string | null;
}

export interface AdminProfileSummary {
  id: string;
  name: string;
  avatar: string;
  avatarUrl: string | null;
  isKids: boolean;
  hasPin: boolean;
  online: boolean;
  status: string | null;
  watching: { title: string | null; mediaId: string } | null;
  /** Restricted in the admin panel: this profile can't use Tea+. reason is only for the owner. */
  restricted: { at: string; reason: string | null } | null;
  /** Friends see a Staff tag on this profile. */
  staff: boolean;
  /** The app this profile last used and whether it's up to date. Null until it's used again. */
  app: AdminProfileApp | null;
  createdAt: string;
}

export interface AdminProfileApp {
  client: 'desktop' | 'web';
  /** The Windows app's version; null for the web app. */
  version: string | null;
  /**
   * latest or outdated against the newest release; blocked when older than the minimum version,
   * so it can't be used until it updates; web (always current); unknown when there's no release
   * to compare with.
   */
  status: 'latest' | 'outdated' | 'blocked' | 'web' | 'unknown';
  latest: string | null;
  seenAt: string;
}

export interface AdminDevice extends AdminDeviceInfo {
  id: string;
  ip: string | null;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
  persistent: boolean;
  activeProfile: string | null;
  current: boolean;
}

export interface AdminTimelineItem {
  id: string;
  at: string;
  kind: string;
  text: string;
  profile: string | null;
  meta?: Record<string, unknown> | null;
}

export interface AdminUserDetail {
  user: {
    id: string;
    email: string;
    displayName: string;
    status: string;
    statusReason: string | null;
    statusChangedAt: string | null;
    staff: boolean;
    createdAt: string;
    updatedAt: string;
    lastLoginAt: string | null;
    onboardingCompleted: boolean;
    /** False for accounts made with Discord that haven't set a password. */
    hasPassword: boolean;
    discord: { id: string; username: string | null; linkedAt: string | null } | null;
  };
  profiles: AdminProfileSummary[];
  devices: AdminDevice[];
  provider: {
    provider: string;
    status: string;
    connectedAt: string;
    lastValidatedAt: string | null;
    lastError: string | null;
  } | null;
  addons: {
    id: string;
    addonId: string;
    name: string;
    version: string;
    enabled: boolean;
    installedAt: string;
    lastSuccessAt: string | null;
    lastError: string | null;
  }[];
  stats: {
    watchSeconds: number;
    measuredWatchSeconds: number;
    plays: number;
    titles: number;
    completed: number;
    ratings: number;
    friends: number;
    activeDays: number;
  };
  activity: SeriesPoint[];
  telemetrySince: string;
}

export interface AdminHistoryItem {
  id: string;
  mediaId: string;
  contentType: string;
  title: string;
  posterUrl: string | null;
  season: number | null;
  episode: number | null;
  episodeTitle: string | null;
  position: number;
  duration: number;
  completed: boolean;
  watchCount: number;
  lastWatchedAt: string;
}

export interface AdminPlaybackRow {
  id: string;
  mediaId: string;
  contentType: string;
  title: string | null;
  season: number | null;
  episode: number | null;
  profileId: string;
  profileName: string;
  userId: string;
  email: string;
  addonName: string | null;
  provider: string | null;
  client: string;
  appVersion: string | null;
  os: string | null;
  startedAt: string;
  endedAt: string | null;
  watchedSeconds: number;
  position: number;
  duration: number;
  completed: boolean;
  status: string;
  endReason: string | null;
  bufferCount: number;
  bufferSeconds: number;
  errorCount: number;
  lastError: string | null;
}

export interface AdminProfileDetail extends TrackedSince {
  profile: AdminProfileSummary & {
    userId: string;
    email: string;
    /** The account's main profile: its Staff setting is the account's. */
    main: boolean;
    language: string;
    maturityLimit: number;
    canManageProfiles: boolean;
    friendCode: string | null;
    preferences: Record<string, unknown>;
  };
  nowPlaying: LivePlayback | null;
  stats: {
    watchSeconds: number;
    measuredWatchSeconds: number;
    films: number;
    series: number;
    episodes: number;
    rewatches: number;
    downloads: number;
    plays: number;
    completionRate: number | null;
    averageSessionSeconds: number | null;
    ratings: number;
    friends: number;
  };
  continueWatching: AdminHistoryItem[];
  watchlist: {
    mediaId: string;
    contentType: string;
    title: string;
    posterUrl: string | null;
    addedAt: string;
  }[];
  favourites: {
    mediaId: string;
    contentType: string;
    title: string;
    posterUrl: string | null;
    stars: number | null;
  }[];
  topTitles: Ranked[];
  activity: SeriesPoint[];
  hours: number[];
}

export interface AdminAddonRow {
  addonId: string;
  name: string;
  version: string;
  host: string | null;
  installs: number;
  users: number;
  enabled: number;
  withErrors: number;
  resolveOk: number;
  resolveFail: number;
  successRate: number | null;
  plays: number;
  removals: number;
  firstInstalledAt: string;
  lastError: string | null;
}

export interface AdminAddonDetail extends TrackedSince {
  addon: AdminAddonRow & { description: string | null; types: string[]; resources: string[] };
  installs: SeriesPoint[];
  removals: SeriesPoint[];
  resolves: { ok: SeriesPoint[]; fail: SeriesPoint[] };
  errors: { at: string; message: string; kind: string }[];
  topTitles: Ranked[];
}

export interface AdminAddonsOverview extends TrackedSince {
  addons: AdminAddonRow[];
  adoption: { t: string; v: number; key: string }[];
  totals: { installs: number; users: number; resolveOk: number; resolveFail: number };
}

export interface AdminContentOverview extends TrackedSince {
  range: { from: string; to: string };
  totals: {
    plays: number;
    watchSeconds: number;
    completionRate: number | null;
    abandonmentRate: number | null;
    failures: number;
    bufferEvents: number;
    averageSessionSeconds: number | null;
  };
  topMovies: Ranked[];
  topSeries: Ranked[];
  topEpisodes: Ranked[];
  topDownloads: Ranked[];
  trending: Ranked[];
  historyTitles: Ranked[];
  plays: SeriesPoint[];
  watchHours: SeriesPoint[];
  heatmap: number[][];
  byAddon: {
    key: string;
    plays: number;
    failures: number;
    buffers: number;
    watchSeconds: number;
  }[];
  byClient: {
    key: string;
    plays: number;
    failures: number;
    buffers: number;
    watchSeconds: number;
  }[];
  byVersion: {
    key: string;
    plays: number;
    failures: number;
    buffers: number;
    watchSeconds: number;
  }[];
}

export interface AdminDevicesOverview extends TrackedSince {
  activeSessions: number;
  activeDevices24h: number;
  clients: Ranked[];
  os: Ranked[];
  browsers: Ranked[];
  versions: Ranked[];
  platformTrend: { t: string; desktop: number; web: number }[];
  issuesByDevice: { key: string; plays: number; errors: number; buffers: number }[];
  clientErrors: {
    id: string;
    at: string;
    kind: string;
    message: string;
    where: string | null;
    client: string | null;
    appVersion: string | null;
    user: string | null;
  }[];
  latestVersion: string | null;
  minVersion: string;
}

export interface AdminAlert {
  id: string;
  level: 'warning' | 'critical';
  title: string;
  detail: string;
}

export interface AdminSystemStatus extends TrackedSince {
  at: string;
  commit: string | null;
  node: string;
  uptimeSeconds: number;
  startedAt: string;
  database: {
    dialect: string;
    pingMs: number | null;
    sizeBytes: number | null;
    tables: { name: string; rows: number }[];
  };
  process: {
    rss: number;
    heapUsed: number;
    heapTotal: number;
    cpuPercent: number;
    eventLoopP99Ms: number;
  };
  host: {
    cpus: number;
    load: number[];
    memTotal: number;
    memFree: number;
    cpuPercent: number;
    diskTotal: number | null;
    diskFree: number | null;
    platform: string;
  };
  api: {
    rpm: number;
    p50: number;
    p95: number;
    errorRate: number;
    lastHour: { requests: number; errors: number; avgMs: number };
  };
  minutes: {
    t: string;
    requests: number;
    errors: number;
    p95: number;
    cpu: number | null;
    rss: number | null;
  }[];
  hourly: { t: string; requests: number; errors: number; avgMs: number }[];
  routes: {
    route: string;
    count: number;
    errors: number;
    avgMs: number;
    p95: number;
    maxMs: number;
  }[];
  availability: { last24h: number | null; last7d: number | null; last30d: number | null };
  services: { name: string; ok: boolean; detail: string }[];
  alerts: AdminAlert[];
  errors: {
    id: string;
    at: string;
    route: string | null;
    message: string;
    code: string | null;
    status: number | null;
  }[];
}

export interface AdminAuditRow {
  id: string;
  at: string;
  action: string;
  target: string | null;
  meta: Record<string, unknown> | null;
  ip: string | null;
  userAgent: string | null;
  current: boolean;
}

export interface AdminSecurityOverview {
  sessions: {
    id: string;
    createdAt: string;
    lastSeenAt: string;
    expiresAt: string;
    ip: string | null;
    device: string;
    current: boolean;
  }[];
  passkeys: AdminPasskey[];
  logins: AdminAuditRow[];
  suspicious: {
    userId: string | null;
    email: string | null;
    reason: string;
    count: number;
    lastAt: string;
  }[];
  restricted: {
    id: string;
    email: string;
    status: string;
    reason: string | null;
    changedAt: string | null;
  }[];
  config: { allowlist: boolean; sessionHours: number; idleMinutes: number; origin: string };
}

export interface AdminAnnouncement {
  id: string;
  title: string;
  body: string;
  level: AnnouncementLevel;
  audience: AnnouncementAudience;
  active: boolean;
  dismissible: boolean;
  startsAt: string | null;
  endsAt: string | null;
  createdAt: string;
  updatedAt: string;
  live: boolean;
}

export interface AdminPlatform {
  flags: PlatformFlags;
  maintenance: MaintenanceState;
  minDesktopVersion: { effective: string; override: string | null; configured: string };
  releases: { latest: string | null; files: { name: string; size: number; modifiedAt: string }[] };
  announcements: AdminAnnouncement[];
  retention: { telemetryDays: number; playbackDays: number; metricsDays: number };
}

export interface AdminSearchResult {
  users: { id: string; email: string; displayName: string; status: string }[];
  profiles: { id: string; name: string; userId: string; email: string; avatar: string }[];
  titles: { mediaId: string; title: string; contentType: string; viewers: number }[];
}

export interface AdminFriend {
  friendshipId: string;
  profileId: string;
  name: string;
  avatar: string;
  avatarUrl: string | null;
  userId: string;
  email: string;
  staff: boolean;
  /** friends, or a request this profile sent or received. */
  status: 'friends' | 'sent' | 'received';
  since: string;
  sharesActivity: boolean;
  online: boolean;
  watching: string | null;
  recommendationsSent: number;
  recommendationsReceived: number;
}

export interface AdminActivityItem {
  id: string;
  at: string;
  kind: string;
  text: string;
  mediaId: string | null;
  title: string | null;
  posterUrl: string | null;
  /** tracked: recorded as it happened; history: worked out from saved data (before tracking). */
  source: 'tracked' | 'history';
}

export interface AdminDownloads extends TrackedSince {
  /** Every download ever started (counted since 0.5.20). */
  total: number;
  items: {
    id: string;
    at: string;
    mediaId: string | null;
    title: string | null;
    contentType: string | null;
    season: number | null;
    episode: number | null;
    posterUrl: string | null;
    client: string | null;
    appVersion: string | null;
  }[];
}

export interface AdminPartyMember {
  profileId: string;
  userId: string;
  email: string | null;
  name: string;
  avatar: string;
  avatarUrl: string | null;
  isKids: boolean;
  isHost: boolean;
  connected: boolean;
  ready: boolean;
  buffering: boolean;
  sittingOut: boolean;
  lastSeen: string;
}

export interface AdminParty {
  code: string;
  status: string;
  media: {
    type: string;
    id: string;
    season: number | null;
    episode: number | null;
    title: string;
    posterUrl: string | null;
    year: number | null;
  };
  hasTitle: boolean;
  /** What `media` is to this party: playing or picked, up next in the queue, or last watched. */
  titleRole: 'current' | 'next' | 'last' | null;
  createdAt: string;
  endedAt: string | null;
  endedReason: string | null;
  host: { profileId: string; userId: string; name: string } | null;
  position: number;
  pause: { by: string; reason: string } | null;
  hostStream: string | null;
  settings: { hostOnlyControls: boolean; locked: boolean };
  readyCheck: boolean;
  pollOptions: number;
  chatMessages: number;
  listeners: number;
  members: AdminPartyMember[];
}

export interface AdminPartiesOverview extends TrackedSince {
  enabled: boolean;
  now: { parties: number; people: number; connected: number; playing: number };
  totals: {
    started: number;
    joins: number;
    ended: number;
    averageMinutes: number | null;
    averagePeople: number | null;
  };
  series: { created: SeriesPoint[]; joined: SeriesPoint[] };
  topTitles: Ranked[];
  parties: AdminParty[];
  recent: {
    id: string;
    at: string;
    kind: string;
    who: string | null;
    profileId: string | null;
    text: string;
  }[];
}
